import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Target, Activity, Brain, ChevronRight, Star, AlertCircle, Eye, Heart, Moon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Logo } from '../components/Logo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BriefingReturnPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/demo/briefing/${token}`);
        setData(res.data);
      } catch {
        setError('Briefing not found or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <Logo className="h-10 mb-6" />
        <h1 className="text-xl font-bold mb-2">Briefing Not Found</h1>
        <p className="text-muted-foreground text-sm mb-6">{error}</p>
        <Button onClick={() => navigate('/event')} className="rounded-full">Start New Briefing</Button>
      </div>
    );
  }

  const { briefing, dts_score, vitals, intake, first_name, created_at } = data;
  const getDtsColor = (s) => (s >= 82 ? '#10b981' : s >= 76 ? '#f59e0b' : '#ef4444');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-background p-6 pb-32">
      <div className="max-w-md mx-auto">
        <div className="flex justify-center pt-2 mb-6"><Logo className="h-10" /></div>

        <div className="text-center mb-6">
          <Badge className="bg-primary/10 text-primary border-0 mb-3 inline-flex items-center gap-1">
            <Sparkles size={12} /> Body Briefing
          </Badge>
          <h1 className="text-2xl font-bold mb-1" data-testid="briefing-return-greeting">
            Welcome back, {first_name}!
          </h1>
          {created_at && (
            <p className="text-sm text-muted-foreground">
              Created {new Date(created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          )}
        </div>

        {/* DTS Score */}
        {dts_score && (
          <div className="flex justify-center mb-6">
            <div className="relative w-32 h-32">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="44" fill="none" stroke="hsl(var(--muted))" strokeWidth="6" opacity="0.3" />
                <circle cx="50" cy="50" r="44" fill="none" stroke={getDtsColor(dts_score)} strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={`${(dts_score / 100) * 276.46} 276.46`} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-black" style={{ color: getDtsColor(dts_score) }}>{dts_score}</span>
                <span className="text-[10px] text-muted-foreground">DTS</span>
              </div>
            </div>
          </div>
        )}

        {/* Quick vitals */}
        {vitals && (
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Mood', value: intake?.mood ? `${intake.mood}/10` : '--', icon: Brain },
              { label: 'Sleep', value: intake?.sleep_quality || '--', icon: Moon },
              { label: 'HR', value: vitals.heart_rate || '--', icon: Heart },
              { label: 'SpO2', value: vitals.spo2 ? `${vitals.spo2}%` : '--', icon: Activity },
            ].map(v => (
              <div key={v.label} className="text-center">
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center mx-auto mb-1">
                  <v.icon size={14} className="text-primary" />
                </div>
                <p className="text-sm font-bold">{v.value}</p>
                <p className="text-[10px] text-muted-foreground">{v.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        <Card className="p-5 mb-5 border-l-4 border-l-primary bg-card" data-testid="return-briefing-summary">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} className="text-primary" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Your Body Today</span>
          </div>
          <p className="text-sm leading-relaxed">{briefing?.summary}</p>
        </Card>

        {/* Signals */}
        {briefing?.signals?.length > 0 && (
          <Card className="p-5 mb-5 bg-card">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={16} className="text-amber-500" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">What We're Noticing</span>
            </div>
            <ul className="space-y-2">
              {briefing.signals.map((s, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-amber-500 mt-1 shrink-0">&#8226;</span> {s}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Actions */}
        {briefing?.actions?.length > 0 && (
          <div className="mb-5">
            <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Target size={14} className="text-primary" /> Your Next Best Steps
            </h3>
            <div className="space-y-2.5">
              {briefing.actions.map((a, i) => (
                <Card key={i} className="p-4 bg-card">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-primary">{i + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{a.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{a.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Closing */}
        {briefing?.closing && (
          <Card className="p-4 bg-gradient-to-br from-amber-500/5 to-primary/5 border-l-4 border-l-amber-500 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Star size={14} className="text-amber-500" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Coach360 Note</span>
            </div>
            <p className="text-sm">{briefing.closing}</p>
          </Card>
        )}

        <Card className="p-4 bg-gradient-to-br from-primary/5 to-amber-500/5 mb-6">
          <div className="flex items-center gap-3">
            <Star size={18} className="text-amber-500" />
            <div className="text-left">
              <p className="font-medium text-sm">Founding Member</p>
              <p className="text-xs text-muted-foreground">Early access secured</p>
            </div>
            <Badge className="ml-auto bg-amber-500/10 text-amber-600 border-0">Active</Badge>
          </div>
        </Card>

        <Button onClick={() => navigate('/register')} className="w-full rounded-full h-13" data-testid="create-account-btn">
          Create Full Account <ChevronRight size={18} className="ml-2" />
        </Button>
      </div>
    </motion.div>
  );
}
